import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Button,
  Stack,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { 
  Business, 
  ProductionQuantityLimits, 
  Payment, 
  Support,
  PrecisionManufacturing,
  LocalShipping,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../config/brand';
import { BrandHero } from '../components/brand/BrandHero';

interface FAQCategory {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

const faqCategories: FAQCategory[] = [
  {
    title: 'About Monio',
    subtitle: 'Learn about our company and services',
    description: 'Get to know Monio Manufacturing and what we do',
    icon: <Business sx={{ fontSize: 40, color: '#6366f1' }} />,
    questions: [
      {
        question: 'What is Monio Manufacturing?',
        answer: 'Monio Manufacturing is a leading African manufacturing company specializing in premium textiles, smart furniture, and advanced metalworks. We combine traditional craftsmanship with cutting-edge technology to deliver exceptional products.',
      },
      {
        question: 'Where is Monio located?',
        answer: 'Our headquarters and main manufacturing facilities are located in Nairobi, Kenya. We serve clients across Africa and internationally.',
      },
      {
        question: 'How long has Monio been in business?',
        answer: 'Monio was founded in 2020 and has grown rapidly to become one of the leading manufacturing companies in Africa, serving hundreds of satisfied clients.',
      },
      {
        question: 'What industries do you serve?',
        answer: 'We serve a wide range of industries including fashion and textiles, furniture and home goods, automotive, construction, and industrial manufacturing.',
      },
    ],
  },
  {
    title: 'Our Services',
    subtitle: 'Manufacturing capabilities and offerings',
    description: 'Explore our comprehensive manufacturing services',
    icon: <ProductionQuantityLimits sx={{ fontSize: 40, color: '#06b6d4' }} />,
    questions: [
      {
        question: 'What manufacturing services does Monio offer?',
        answer: 'We offer custom textile manufacturing, smart furniture production, precision metalworks, supply chain solutions, quality assurance services, and custom manufacturing solutions tailored to your specific needs.',
      },
      {
        question: 'Do you offer custom manufacturing?',
        answer: 'Yes, we specialize in custom manufacturing solutions. We work closely with clients to understand their requirements and create tailored products that meet their exact specifications.',
      },
      {
        question: 'What types of textiles do you manufacture?',
        answer: 'We manufacture a wide range of textiles including natural fibers, synthetic materials, and sustainable fabrics for fashion, industrial, and commercial applications.',
      },
      {
        question: 'Can you handle large-scale production orders?',
        answer: 'Absolutely. Our facilities are equipped to handle both small custom orders and large-scale production runs. We can produce up to 10,000 units per month depending on the product type.',
      },
    ],
  },
  {
    title: 'Quality & Standards',
    subtitle: 'Our commitment to excellence',
    description: 'Learn about our quality assurance processes',
    icon: <PrecisionManufacturing sx={{ fontSize: 40, color: '#f59e0b' }} />,
    questions: [
      {
        question: 'What quality standards does Monio follow?',
        answer: 'We adhere to international quality standards including ISO certifications. Our quality assurance system ensures every product meets the highest standards of excellence.',
      },
      {
        question: 'How do you ensure product quality?',
        answer: 'We have a comprehensive quality control process that includes material inspection, in-process quality checks, and final product testing. Our quality team monitors every stage of production.',
      },
      {
        question: 'Do you provide quality certifications?',
        answer: 'Yes, we provide quality certifications for all our products. We can also arrange third-party testing and certification if required by your industry standards.',
      },
      {
        question: 'What is your defect rate?',
        answer: 'Our defect rate is consistently below 0.2%, which is well above industry standards. We maintain this through rigorous quality control processes and continuous improvement programs.',
      },
    ],
  },
  {
    title: 'Pricing & Quotes',
    subtitle: 'Cost and payment information',
    description: 'Understand our pricing structure and payment terms',
    icon: <Payment sx={{ fontSize: 40, color: '#10b981' }} />,
    questions: [
      {
        question: 'How do you determine pricing?',
        answer: 'Our pricing is based on several factors including material costs, production complexity, order quantity, and delivery timeline. We provide transparent, competitive pricing for all projects.',
      },
      {
        question: 'Do you offer volume discounts?',
        answer: 'Yes, we offer volume discounts for larger orders. The discount structure varies by product type and order quantity. Contact us for specific pricing information.',
      },
      {
        question: 'What payment terms do you offer?',
        answer: 'We offer flexible payment terms including upfront payment, milestone payments, and net 30 terms for established clients. Payment terms are negotiated based on project scope and client relationship.',
      },
      {
        question: 'Do you provide free quotes?',
        answer: 'Yes, we provide free, no-obligation quotes for all projects. Simply contact us with your requirements and we\'ll provide a detailed quote within 24 hours.',
      },
    ],
  },
  {
    title: 'Shipping & Delivery',
    subtitle: 'Logistics and delivery information',
    description: 'Learn about our shipping and delivery processes',
    icon: <LocalShipping sx={{ fontSize: 40, color: '#8b5cf6' }} />,
    questions: [
      {
        question: 'Where do you ship to?',
        answer: 'We ship to clients across Africa and internationally. We have established logistics partnerships to ensure reliable delivery to any location.',
      },
      {
        question: 'How long does delivery take?',
        answer: 'Delivery times vary by location and product type. Local delivery typically takes 2-5 days, while international shipping can take 1-3 weeks. We provide specific delivery timelines with each quote.',
      },
      {
        question: 'Do you handle customs and import duties?',
        answer: 'We can assist with customs documentation and import procedures. However, import duties and taxes are typically the responsibility of the client. We provide all necessary documentation.',
      },
      {
        question: 'What shipping methods do you offer?',
        answer: 'We offer various shipping methods including air freight, sea freight, and land transportation. We work with reliable logistics partners to ensure safe and timely delivery.',
      },
    ],
  },
  {
    title: 'Support & Service',
    subtitle: 'Customer support and after-sales service',
    description: 'Get information about our customer support',
    icon: <Support sx={{ fontSize: 40, color: '#ef4444' }} />,
    questions: [
      {
        question: 'What kind of support do you provide?',
        answer: 'We provide comprehensive support including technical consultation, product training, installation assistance, and ongoing maintenance support. Our team is available to help throughout the entire process.',
      },
      {
        question: 'Do you offer warranty on your products?',
        answer: 'Yes, we provide warranty coverage on all our products. Warranty terms vary by product type and typically range from 1-3 years. We also offer extended warranty options.',
      },
      {
        question: 'How can I contact your support team?',
        answer: 'You can contact our support team via phone, email, or through our website contact form. We typically respond to inquiries within 24 hours.',
      },
      {
        question: 'Do you provide training for your products?',
        answer: 'Yes, we provide training for complex products and systems. This includes installation training, operation training, and maintenance training to ensure optimal performance.',
      },
    ],
  },
];

export const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BRAND.colors.creamLight }}>
      <BrandHero
        title="Frequently Asked Questions"
        subtitle={BRAND.tagline}
        description="Answers about Monio Cluster manufacturing services, orders, and support."
        backgroundImage={BRAND.images.outdoorSign}
      />

      <Container maxWidth="lg">
        {faqCategories.map((category, index) => (
          <Box key={index} sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box sx={{ mb: 3 }}>
                {category.icon}
              </Box>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
                {category.title}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {category.subtitle}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                {category.description}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {category.questions.map((faq, qIndex) => (
                <Grid item xs={12} md={6} key={qIndex}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 3,
                      boxShadow: '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0px 25px 50px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Accordion 
                      expanded={expanded === `panel${index}-${qIndex}`}
                      onChange={handleChange(`panel${index}-${qIndex}`)}
                      sx={{ 
                        background: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            margin: 0,
                          },
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Contact Section */}
        <Box sx={{ 
          mt: 8, 
          p: 6, 
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
          borderRadius: 3,
          textAlign: 'center',
        }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Still Have Questions?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
            Our team is here to help. Contact us for personalized assistance with your manufacturing needs.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                },
              }}
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 6,
                py: 2,
                borderRadius: 3,
                border: '2px solid #6366f1',
                color: '#6366f1',
                '&:hover': {
                  border: '2px solid #4f46e5',
                  background: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              Request Quote
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};
